import PropTypes from 'prop-types';
import { Box, Button, Card, Skeleton, Tooltip } from '@material-ui/core';
import { GetAppOutlined } from '@material-ui/icons';
import Icon from '@iconify/react';
import filePdfIcon from '@iconify/icons-mdi/file-pdf';
import { useSnackbar } from 'notistack';
import { DialogAnimate } from 'src/components/animate';
import {
  downloadFileFromUrl,
  downloadImage,
  openFileInViewer,
  isPdfFile,
  isImageFile,
  isOfficeFile,
  getEmbeddedFileSource,
  getFileExtension,
  getAttachmentDownloadUrl,
  getAttachmentDisplayName,
} from 'src/Helpers/FileDownloadUtils';

AttachmentPreviewDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  fileUrl: PropTypes.string,
  file: PropTypes.object,
  isClient: PropTypes.bool,
  userType: PropTypes.number,
  pdfLoading: PropTypes.bool,
  setPdfLoading: PropTypes.func,
  useOfficeViewer: PropTypes.bool,
  setUseOfficeViewer: PropTypes.func,
  headerStart: PropTypes.node,
};

export default function AttachmentPreviewDialog({
  open,
  onClose,
  fileUrl,
  file = null,
  isClient = false,
  userType,
  pdfLoading,
  setPdfLoading,
  useOfficeViewer,
  setUseOfficeViewer,
  headerStart = null,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const isRestrictedUser = isClient || userType === 3;
  const canDownload = !isRestrictedUser;
  const fileType = file?.file_type || file?.type || '';

  const downloadUrl = getAttachmentDownloadUrl(file) || fileUrl;
  const displayName = getAttachmentDisplayName(file);

  const handleDownload = () => {
    if (!downloadUrl) {
      enqueueSnackbar('No file to download', { variant: 'warning' });
      return;
    }
    const onSuccess = () =>
      enqueueSnackbar('File downloaded successfully', { variant: 'success' });
    const onError = () =>
      enqueueSnackbar('Failed to download file', { variant: 'error' });

    if (isImageFile(fileType) || ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(getFileExtension(downloadUrl))) {
      downloadImage(downloadUrl, displayName, 0, onSuccess, onError);
      return;
    }
    downloadFileFromUrl(downloadUrl, displayName, onSuccess, onError);
  };

  const handleView = () => {
    openFileInViewer(downloadUrl || fileUrl, {
      isActualPdf: isPdfFile(fileUrl, fileType),
      isOfficeFile: isOfficeFile(fileUrl),
      isClient: isRestrictedUser,
      hideToolbar: true,
      fileName: file?.name || fileUrl?.split('/').pop()?.split('?')[0] || 'File',
      fileType,
    });
  };

  const renderPreview = () => {
    if (!fileUrl) return null;

    const fileName = fileUrl.split('/').pop()?.split('?')[0] || '';
    const fileExt = fileName.split('.').pop()?.toLowerCase();
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(fileExt);
    const isOffice = isOfficeFile(fileUrl);
    const isDesign = ['ai', 'psd', 'eps', 'dwg', 'dxf', 'tif', 'tiff', 'bmp'].includes(fileExt);

    if (isImage) {
      return (
        <Box
          component="img"
          src={fileUrl}
          alt="Preview"
          sx={{
            maxWidth: '100%',
            maxHeight: 500,
            objectFit: 'contain',
            display: pdfLoading ? 'none' : 'block',
          }}
          onLoad={() => setPdfLoading(false)}
        />
      );
    }

    if (isOffice) {
      return (
        <iframe
          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`}
          title="Office Viewer"
          width="100%"
          height="500px"
          frameBorder="0"
          style={{ display: pdfLoading ? 'none' : 'block', border: 'none' }}
          onLoad={() => setPdfLoading(false)}
        />
      );
    }

    if (isDesign || isRestrictedUser) {
      const embeddedSrc = getEmbeddedFileSource(fileUrl, {
        isActualPdf: fileExt === 'pdf',
        hideToolbar: true,
      });
      return (
        <iframe
          src={embeddedSrc}
          title="File Viewer"
          width="100%"
          height="500px"
          frameBorder="0"
          style={{ display: pdfLoading ? 'none' : 'block', border: 'none' }}
          onLoad={() => setPdfLoading(false)}
          onError={() => {
            setUseOfficeViewer(false);
            setPdfLoading(false);
          }}
        />
      );
    }

    return (
      <iframe
        src={fileUrl}
        title="File Viewer"
        width="100%"
        height="500px"
        frameBorder="0"
        style={{ display: pdfLoading ? 'none' : 'block', border: 'none' }}
        onLoad={() => setPdfLoading(false)}
      />
    );
  };

  return (
    <DialogAnimate
      open={open}
      onClose={() => {
        onClose();
      }}
    >
      <Card
        sx={{
          p: 3,
          maxWidth: 768,
          margin: 'auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: '90%',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            mb: 2,
            ml: 3,
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          {headerStart}
          {canDownload && (
            <Tooltip title="Download File">
              <Button
                variant="outlined"
                color="primary"
                onClick={handleDownload}
                disabled={!fileUrl}
                startIcon={<GetAppOutlined />}
                sx={{ fontSize: '14px' }}
              >
                Download
              </Button>
            </Tooltip>
          )}
          <Button
            variant="outlined"
            color="primary"
            onClick={handleView}
            disabled={!fileUrl}
            startIcon={<Icon icon={filePdfIcon} width={20} height={20} />}
            sx={{ fontSize: '14px' }}
          >
            {isPdfFile(fileUrl, fileType)
              ? 'View PDF'
              : isImageFile(fileType)
                ? 'View Image'
                : 'View File'}
          </Button>
        </Box>
        {pdfLoading && (
          <Skeleton variant="rectangular" width="100%" height={500} />
        )}
        {renderPreview()}
      </Card>
    </DialogAnimate>
  );
}
